<template>
  <div class="v3-form adult-form">
    <div class="row">
      <div>
        <LazyLoadSelect
          data-testid="lazy-load-select"
          :is-multiple="true"
          class="flex-1 me-2"
          :selected="state.form.locations"
          type="locations"
          :added-options="addedLocations"
          placeholder="Select locations"
          @update:model-value="(value) => updateData(value)"
        />
        <div class="info-box mt-4">
          <InfoBox
            data-testid="info-box"
            message="Specify which locations (rooms) should be available for Auto passes."
          />
        </div>
      </div>
      <div>
        <InfoBox
          v-if="state.serverRes"
          class="mt-4"
          :class="{ danger: Object.keys(state.serverErrors).length }"
          :title="state.serverRes.title"
          announce-assertive
          :message="state.serverRes.message"
        />
      </div>
      <div class="text-center">
        <LoadingButton class="me-2 px-4" @click="onCancel" rounded
          >Cancel</LoadingButton
        >
        <LoadingButton
          data-testid="loading-button"
          :is-loading="state.isProcessing"
          @click="submit()"
          class="mt-4 px-4"
          solid
          rounded
          >Allow</LoadingButton
        >
      </div>
    </div>
  </div>
</template>

<script>
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import LazyLoadSelect from "@/v3components/shared/Form/LazyLoadSelect.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import useVuelidate from "@vuelidate/core"
import { helpers, required } from "@vuelidate/validators"
import { useStore } from "vuex"
import { reactive, computed, onMounted, ref } from "vue"

export default {
  name: "AutoPassModalForm",
  components: {
    LoadingButton,
    InfoBox,
    LazyLoadSelect
  },
  emits: ["cancel"],
  setup(props, { emit }) {
    const store = useStore()
    const state = reactive({
      isProcessing: false,
      serverErrors: {},
      serverRes: null,
      form: {
        locations: null
      }
    })

    const addedLocations = ref([])

    const validationMessages = {
      required: "This field is required"
    }
    const validations = {
      form: {
        locations: {
          required: helpers.withMessage(validationMessages.required, required)
        }
      }
    }
    const v$ = useVuelidate(validations.form, state.form)

    const isFormValid = computed(() => !v$.value.$invalid)

    const errors = computed(() => {
      const errorObj = {}
      v$.value.$errors.forEach((err) => {
        errorObj[err.$property] = err.$message
      })

      return errorObj
    })

    const updateData = (value) => {
      state.form.locations = value
      state.serverErrors = {}
      state.serverRes = null
    }

    const submit = () => {
      if (isFormValid.value) {
        // Extract just the IDs from the selected location objects
        const selectedIds = Array.isArray(state.form.locations)
          ? state.form.locations.map(
              (location) => location.value?.id || location.id || location
            )
          : [
              state.form.locations.value?.id ||
                state.form.locations.id ||
                state.form.locations
            ]

        const data = {
          autopass_rooms: selectedIds
        }
        allowLocations(data)
      }
    }

    const allowLocations = (data) => {
      state.isProcessing = true
      store
        .dispatch("rooms/setAutoPassLimitV7", data)
        .then(() => {
          setResponseInfoBox("Success", "Successfully saved!")
          state.isProcessing = false
          state.serverErrors = {}
          setTimeout(() => {
            setResponseInfoBox()
            store.dispatch("rooms/getAutoPassLimitV7")
            emit("cancel")
          }, 1800)
        })
        .catch((err) => {
          const res = err.response.data
          state.serverErrors = res.errors ? res.errors : {}
          setResponseInfoBox("Error", res.message)
          state.isProcessing = false
        })
    }

    const setResponseInfoBox = (title, message) => {
      if (title || message) {
        state.serverRes = {
          message,
          title
        }
      } else {
        state.serverRes = null
      }
    }

    const onCancel = () => {
      resetFormData()
      emit("cancel")
    }

    const resetFormData = () => {
      state.form.locations = null
    }

    onMounted(() => {
      store
        .dispatch("rooms/getAutoPassLimitLocations")
        .then((resp) => {
          const result = resp?.data?.rooms || []
          addedLocations.value = result.filter(Boolean).map((location) => {
            if (typeof location === "object" && location !== null) {
              return {
                label: location.name || `Location ID: ${location.id}`,
                value: {
                  id: location.id,
                  type: "App\\Models\\Room"
                }
              }
            } else {
              return {
                label: `Location ID: ${location}`,
                value: {
                  id: location,
                  type: "App\\Models\\Room"
                }
              }
            }
          })
        })
        .catch(() => {
          addedLocations.value = []
        })
    })

    return {
      state,
      v$,
      updateData,
      submit,
      isFormValid,
      errors,
      onCancel,
      resetFormData,
      setResponseInfoBox,
      addedLocations
    }
  }
}
</script>
